/**
 * Mock — conversations: drill-down lists, post breakdowns, Tune sentiment.
 *
 * Tune sentiment's corrections are the one piece of state every widget reads,
 * so they live in the shared `mock/db.ts` (which `query.ts` applies to every
 * row), not in a conversations-only db.
 */
import type {
  ConversationRow,
  ConversationSort,
  ConversationsApi,
  PostBreakdown,
} from '@/data-sources/contract';

import { dimensionIndex, labelOf } from '../engine/dimensions';
import type { MentionRow } from '../engine/generator';
import { postCopy } from '../engine/pageConversations';
import { postBreakdown } from '../engine/postBreakdown';
import { postedAt } from '../engine/posted';
import { postUrl } from '../engine/posts';
import { allRows } from '../engine/rows';
import { applySentimentOverrides, rowId } from '../engine/sentimentOverrides';
import { db, saveSentimentOverrides } from '../db';
import { simulate } from '../scenarios';
import { rowsOfTarget } from './resolve';

/** `conversationsOf`, with option keys where the prototype stored labels. */
export function conversationOf(row: MentionRow): ConversationRow {
  return {
    id: rowId(row),
    text: postCopy(row),
    url: postUrl(row),
    channel: row.channel,
    sentiment: row.sentiment,
    intent: row.intent,
    topic: row.topic,
    reactions: row.reactions + row.likes,
    comments: row.comments,
    shares: row.shares,
    engagement: row.engagement,
    postedAt: new Date(postedAt(row)).toISOString(),
    type: row.conversation,
  };
}

/** `ConversationsPanel`'s `compare`, on corpus rows. */
function compare(a: MentionRow, b: MentionRow, key: ConversationSort['key']): number {
  switch (key) {
    case 'sentiment':
      /* The chips' own order — positive, neutral, negative. */
      return (
        (dimensionIndex.sentiment[a.sentiment] ?? 0) - (dimensionIndex.sentiment[b.sentiment] ?? 0)
      );
    case 'channel':
      return (dimensionIndex.channel[a.channel] ?? 0) - (dimensionIndex.channel[b.channel] ?? 0);
    case 'topic':
    case 'intent':
      return labelOf(key, a[key]).localeCompare(labelOf(key, b[key]));
    case 'postedAt':
      return postedAt(a) - postedAt(b);
    case 'reactions':
      return a.reactions + a.likes - (b.reactions + b.likes);
    default:
      return a[key] - b[key];
  }
}

/** Sorted exactly as the prototype's panel sorted: stable, engagement tie-break, `desc` = reversed. */
export function sortConversations(
  rows: readonly MentionRow[],
  sort: ConversationSort,
): MentionRow[] {
  const out = [...rows].sort((a, b) => compare(a, b, sort.key) || b.engagement - a.engagement);
  return sort.direction === 'desc' ? out.reverse() : out;
}

export const mockConversationsApi: ConversationsApi = {
  async listConversations({ target, query, page, pageSize, sort }, ctx) {
    // No latency: the prototype opened, sorted and paged this list instantly.
    await simulate('listConversations', ctx.signal, { latency: false });
    const sorted = sortConversations(rowsOfTarget(target, query), sort);
    const size = Math.max(1, pageSize);
    const pageCount = Math.max(1, Math.ceil(sorted.length / size));
    const current = Math.min(Math.max(1, page), pageCount);
    return {
      items: sorted.slice((current - 1) * size, current * size).map(conversationOf),
      total: sorted.length,
      page: current,
      pageSize: size,
    };
  },

  async getPostBreakdown({ postId }, ctx) {
    // No latency: the prototype's breakdown panels drew instantly.
    await simulate('getPostBreakdown', ctx.signal, { latency: false });
    const source = allRows[postId];
    if (!source) throw new Error(`No post with id ${postId}.`);
    /* The post as the dashboard counts it — a Tune sentiment correction included. */
    const [row] = applySentimentOverrides([source], db.sentimentOverrides);
    const b = postBreakdown(row!);
    const breakdown: PostBreakdown = {
      totalComments: b.totalComments,
      relevantComments: b.relevantComments,
      totalShares: b.totalShares,
      commentSentiment: { ...b.commentSentiment },
      topComments: b.topComments.map((c) => ({ ...c })),
      reactions: b.reactions.map((r) => ({
        key: r.type.toLowerCase(),
        label: r.type,
        count: r.count,
      })),
      /* The prototype stores no post summaries: the panel states its figures. */
      summary: null,
    };
    return breakdown;
  },

  async saveSentimentOverrides({ changes }, ctx) {
    // No latency on the save itself; the widgets it invalidates reload with theirs.
    await simulate('saveSentimentOverrides', ctx.signal, { latency: false });
    const merged: Record<number, string> = {};
    for (const change of changes) {
      if (!allRows[change.postId]) throw new Error(`No post with id ${change.postId}.`);
      merged[change.postId] = change.sentiment;
    }
    saveSentimentOverrides(merged);
  },
};
